import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User as UserIcon, Upload, Trash2, ShieldCheck } from "lucide-react";

/**
 * My Profile dialog (2026-08-25).
 *
 * Users edit their own phone + avatar. Email is READ-ONLY (it's their
 * login ID — Admin/Owner change it via the user management screen).
 * "Reports to" is read-only display — only Admin/Owner can (re)assign
 * it via the users PATCH endpoint.
 *
 * 2026-08-26 · Avatar refactor: the image bytes go to Emergent Object
 * Storage via multipart upload (never inline base64 in Mongo). The
 * `avatar_url` returned by the server is a same-origin API path — the
 * `<img src>` loads with the SameSite=None cookies attached so the
 * cross-origin preview host works transparently.
 */
export default function ProfileDialog({ open, onClose }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null);   // server-side URL
  const [previewUrl, setPreviewUrl] = useState(null); // client-side blob preview
  const [pendingFile, setPendingFile] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pendingClear, setPendingClear] = useState(false);
  const fileRef = useRef(null);

  // Backend URL for absolute avatar src (works from any origin).
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";

  const _absoluteAvatarUrl = (relative) => {
    if (!relative) return null;
    // Append cache-buster so the browser reloads after each save.
    const bust = `t=${Date.now()}`;
    const sep = relative.includes("?") ? "&" : "?";
    return `${backendBase}${relative}${sep}${bust}`;
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/auth/me/profile");
        if (cancelled) return;
        const p = r.data?.data || {};
        setProfile(p);
        setPhone(p.phone || "");
        setAvatarUrl(p.avatar_url || null);
        setPreviewUrl(null);
        setPendingFile(null);
        setPendingClear(false);
        setDirty(false);
      } catch (e) {
        toast.error(formatErr(e.response?.data) || e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Clean up any preview blob when it changes / dialog closes.
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const onPickFile = (ev) => {
    const file = ev.target?.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Please pick an image under 2 MB.");
      ev.target.value = "";
      return;
    }
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      toast.error("Only PNG, JPEG or WebP images are supported.");
      ev.target.value = "";
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    setPendingFile(file);
    setPendingClear(false);
    setDirty(true);
    if (ev.target) ev.target.value = "";
  };

  const clearAvatar = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPendingFile(null);
    setAvatarUrl(null);
    setPendingClear(true);
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      // 1) Phone (only patch if changed from server-side value).
      if ((phone.trim() || "") !== (profile?.phone || "")) {
        await api.patch("/auth/me/profile", { phone: phone.trim() });
      }
      // 2) Avatar mutation (upload OR clear).
      if (pendingFile) {
        const fd = new FormData();
        fd.append("file", pendingFile);
        await api.post("/auth/me/avatar", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else if (pendingClear) {
        await api.delete("/auth/me/avatar");
      }
      // 3) Re-fetch /auth/me so the topbar avatar + name reflect the
      // change instantly — no full-page reload needed.
      try { await refresh?.(); } catch { /* non-fatal */ }
      toast.success("Profile saved.");
      setDirty(false);
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data) || e.message);
    } finally { setBusy(false); }
  };

  const initials = (name) => (name || "?")
    .split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const displaySrc = previewUrl || _absoluteAvatarUrl(avatarUrl);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle>My Profile</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                {displaySrc ? (
                  <img
                    src={displaySrc}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                    data-testid="profile-avatar-img"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center text-lg font-semibold text-slate-500">
                    {initials(profile?.full_name)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="profile-avatar-file"
                />
                <Button
                  variant="outline" size="sm"
                  onClick={() => fileRef.current?.click()}
                  data-testid="profile-avatar-upload"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload photo
                </Button>
                {(avatarUrl || previewUrl) ? (
                  <button
                    type="button"
                    onClick={clearAvatar}
                    className="text-xs text-rose-600 hover:underline flex items-center gap-1 self-start"
                    data-testid="profile-avatar-clear"
                  ><Trash2 className="w-3 h-3" /> Remove photo</button>
                ) : null}
                <div className="text-[11px] text-slate-500">
                  PNG, JPEG or WebP. Under 2 MB.
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-700">Full name</Label>
              <Input
                value={profile?.full_name || ""}
                disabled
                data-testid="profile-name"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                Ask your Admin to change your name.
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-700 flex items-center gap-1">
                Email <ShieldCheck className="w-3 h-3 text-slate-400" title="Same as your login ID" />
              </Label>
              <Input
                value={profile?.email || ""}
                disabled
                data-testid="profile-email"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                This is your login ID. Ask your Admin to change it.
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-700">Phone</Label>
              <Input
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setDirty(true); }}
                placeholder="e.g. +971 50 123 4567"
                data-testid="profile-phone"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                Digits, spaces, +, -, () only. 6-20 characters.
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-700">Reports to</Label>
              <div className="mt-1 p-2.5 rounded-md border border-slate-200 bg-slate-50 text-sm"
                   data-testid="profile-reports-to">
                {profile?.reports_to ? (
                  <div className="flex items-center gap-2">
                    <UserIcon className="w-3.5 h-3.5 text-slate-500" />
                    <span className="font-medium">{profile.reports_to.full_name || profile.reports_to.email}</span>
                    {profile.reports_to.designation ? (
                      <span className="text-xs text-slate-500">· {profile.reports_to.designation}</span>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-slate-500">No manager assigned yet.</span>
                )}
                <div className="text-[10px] text-slate-400 mt-1">
                  Only Admin or Owner can (re)assign your manager.
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="profile-cancel">Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || !dirty || loading}
            data-testid="profile-save"
          >{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
